/*
	This is a reducer that ONLY HOUSES ANALYTICS calls.
	It contains no internal state.
	It only subscribes to the actions in the signup ducks file,
	and composes the state to
*/

import moment from "moment";

import Analytics from "./util/Analytics";
import ClientStats from "shared/util/ClientStats";
import isApp from "shared/util/isApp";
import { getValueForPromoDesign } from "analytics/util/queryParamsMap";
import { getQuery } from "analytics/util/helpers";
import sourceFromPathname from "analytics/util/source";
import formattedCameFromParam from "analytics/util/cameFrom";
import UrlHelpers from "shared/util/UrlHelpers";
import Context from "shared/util/Context";

import {
	TOGGLE_VISIBLE,
	PURCHASE_STARTED,
	SUCCESS,
	ERROR,
} from "ducks/payment";

import {
	PRODUCT_SUBTYPES,
	CREDIT_CARD_PAYMENT_PROCESSOR,
	PAYPAL_PAYMENT_PROCESSOR,
} from "shared/payment/constants";
import CUSTOM_EVENT_TYPES from "analytics/util/CustomEventTypes";


// ////////////////////////
// HELPERS ///////////////
// //////////////////////

function getAnalyticsName(product) {
	switch (product.productSubtype) {
		case PRODUCT_SUBTYPES.BOOST: {
			return "Boost Token";
		}

		case PRODUCT_SUBTYPES.INCOGNITO: {
			return "Incognito";
		}

		case PRODUCT_SUBTYPES.ALIST: {
			return "Alist Basic";
		}

		case PRODUCT_SUBTYPES.ALIST_PREMIUM: {
			return "Alist Premium";
		}

		default: {
			return null;
		}
	}
}


export function formatQueryParamsForPurchaseCallback() {
	const data = {};

	// android client is using this query param
	if (UrlHelpers.getParam("promoid")) {
		data["promo id"] = UrlHelpers.getParam("promoid");
	}

	// android client is using this query param
	if (UrlHelpers.getParam("nway")) {
		data.nway = UrlHelpers.getParam("nway");
	}

	// android client is using this query param
	if (UrlHelpers.getParam("promoDesign")) {
		data["promo design"] = UrlHelpers.getParam("promoDesign");
	}

	// why not have duplication?  bc some people also use pd for the
	// promo design query param
	if (UrlHelpers.getParam("pd")) {
		data["promo design"] = getValueForPromoDesign(UrlHelpers.getParam("pd"));
	}

	// android client is using this query param
	if (UrlHelpers.getParam("rateCardVersion")) {
		data["rate card version"] = UrlHelpers.getParam("rateCardVersion");
	}

	return data;
}

export const PRODUCT_NAME_MAP = {
	[PRODUCT_SUBTYPES.BOOST]: "boost",
	[PRODUCT_SUBTYPES.ALIST]: "alist",
	[PRODUCT_SUBTYPES.ALISTPREMIUM]: "alist",
	[PRODUCT_SUBTYPES.INCOGNITO]: "incognito",
	[PRODUCT_SUBTYPES.READ_RECEIPT]: "readreceipt",
};

export const PRODUCT_FEATURE_MAP = {
	[PRODUCT_SUBTYPES.BOOST]: "boost",
	[PRODUCT_SUBTYPES.ALIST]: "alist",
	[PRODUCT_SUBTYPES.ALISTPREMIUM]: "alist premium",
	[PRODUCT_SUBTYPES.INCOGNITO]: "incognito",
	[PRODUCT_SUBTYPES.READ_RECEIPT]: "readreceipt",
};

// ////////////////////////
// REDUCER ///////////////
// //////////////////////

export default function payment(state, action) {
	switch (action.type) {
		case TOGGLE_VISIBLE: {
			const { isVisible } = action.payload;
			const productSubtype = state.product ? state.product.productSubtype : "";

			if (isVisible && productSubtype) {
				const productName = PRODUCT_NAME_MAP[productSubtype];
				const expStatus = Context.get("purchaseExperiments")[productSubtype];
				let platform;

				if (!isApp()) {
					platform = process.env.PLATFORM;
				} else if (window.APP_PLATFORM && window.APP_PLATFORM === "android") {
					platform = "android";
				}

				let statname = `purchases - platform:${platform} - product:${productName} - option selected`;

				if (expStatus) {
					statname = `purchases - platform:${platform} - product:${productName} - state:${expStatus} - option selected`;
				}

				ClientStats.update({
					statname,
					skipDelay: true,
					unique: true,
				});
			}

			return;
		}

		case PURCHASE_STARTED: {
			// sometimes there's no product (because we're finishing a paypal transaction)
			// in that case, don't fire mparticle analytics, since we already did that
			// in analytics/alist_packages
			if (!action.payload || !action.payload.product) {
				return;
			}

			let platform;

			if (!isApp()) {
				platform = process.env.PLATFORM;
			} else if (window.APP_PLATFORM && window.APP_PLATFORM === "android") {
				platform = "android";
			}

			const { product, method } = action.payload;
			const { total, subPeriod, tokensNumber, productSubtype, productType, subtotal } = product;
			const productName = PRODUCT_NAME_MAP[productSubtype];
			const expStatus = Context.get("purchaseExperiments")[productSubtype];

			let statname = `purchases - platform:${platform} - product:${productName} - purchase submitted`;

			if (expStatus) {
				statname = `purchases - platform:${platform} - product:${productName} - state:${expStatus} - purchase submitted`;
			}

			ClientStats.update({
				statname,
				skipDelay: true,
				unique: true,
			});

			let eventName;

			if (method === PAYPAL_PAYMENT_PROCESSOR) {
				eventName = Analytics.eventNames.purchasesSelectSubscribePaypal;
			}

			if (method === CREDIT_CARD_PAYMENT_PROCESSOR) {
				eventName = Analytics.eventNames.purchasesSelectedConfirmPurchaseOnBillingInfo;
			}

			const data = {
				"Gross proceeds in local currency": total / 100,
				"Product": getAnalyticsName(product),
			};

			if (subPeriod) {
				data["Plan duration in months"] = subPeriod;
			}

			if (tokensNumber && productSubtype === PRODUCT_SUBTYPES.BOOST) {
				data["Number of Boost tokens purchased"] = tokensNumber;
			}

			if (eventName) {
				Analytics.logUIEvent(eventName, { data });
			}

			const cameFrom = formattedCameFromParam(PRODUCT_NAME_MAP[productSubtype]);

			// below are analytic events after July 2018
			const analyticData = {
				"feature": PRODUCT_FEATURE_MAP[productSubtype],
				"feature type": productType,
				"rate card name": state.rateCardName,
				"payment type": method === CREDIT_CARD_PAYMENT_PROCESSOR ? "cc" : PAYPAL_PAYMENT_PROCESSOR,
				"came from": cameFrom,
				"promo id": cameFrom,
				source: sourceFromPathname(),
			};

			if (productSubtype === PRODUCT_SUBTYPES.BOOST) {
				analyticData.feature = "boost";
				analyticData["token package"] = tokensNumber;
				analyticData["total token package price"] = subtotal / 100;
			} else {
				const promoDesignQuery = getQuery("pd");
				const promoDesignEventAttr = promoDesignQuery ? getValueForPromoDesign(promoDesignQuery) : "";

				analyticData["total monthly price"] = subtotal / 100;
				analyticData["duration in months"] = subPeriod;
				analyticData["promo design"] = promoDesignEventAttr;
			}

			Analytics.logUIEvent(
				Analytics.eventNames.selectedConfirmPurchaseOnCheckout, {
					data: analyticData,
				},
			);

			return;
		}

		case SUCCESS: {
			if (!action.payload) {
				return;
			}

			const {
				total,
				subPeriod,
				tokensNumber,
				productSubtype,
				productType,
				subtotal,
				discountPercentage,
			} = action.payload;

			const data = {
				"Gross proceeds in local currency": total / 100,
				"Product": PRODUCT_FEATURE_MAP[productSubtype],
			};

			const feature = PRODUCT_FEATURE_MAP[productSubtype];
			const productNameMap = PRODUCT_NAME_MAP[productSubtype];
			const cameFrom = formattedCameFromParam(productNameMap);

			const analyticsData = {
				feature,
				"feature type": productType,
				"rate card name": state.rateCardName,
				status: "success",
				"payment type": action.payload.method === CREDIT_CARD_PAYMENT_PROCESSOR ? "cc" : PAYPAL_PAYMENT_PROCESSOR,
				"promo id": cameFrom,
				"came from": cameFrom,
				source: sourceFromPathname() || getQuery("source"),
				"braze campaign id": getQuery("brazeid"),
			};

			if (discountPercentage) {
				analyticsData["discount applied"] = discountPercentage;
			}

			if (subPeriod) {
				data["duration in months"] = subPeriod;
				data["start date"] = moment().format("l");
				data["end date"] = moment().add(subPeriod, "months").calendar();

				analyticsData["duration in months"] = subPeriod;
				analyticsData["total monthly price"] = subtotal / 100;
			}

			if (tokensNumber && productSubtype === PRODUCT_SUBTYPES.BOOST) {
				data["Number of Boost tokens purchased"] = tokensNumber;
				analyticsData["total token package price"] = subtotal / 100;
				analyticsData["token package"] = tokensNumber;
			}

			if (productSubtype === PRODUCT_SUBTYPES.ALIST
				|| productSubtype === PRODUCT_SUBTYPES.ALIST_PREMIUM) {
				const promoDesignQuery = getQuery("pd");
				const promoDesignEventAttr = promoDesignQuery ? getValueForPromoDesign(promoDesignQuery) : "";

				analyticsData["promo design"] = promoDesignEventAttr;
			}

			let eventName;

			if (productSubtype === PRODUCT_SUBTYPES.INCOGNITO) {
				eventName = Analytics.eventNames.purchasesSubscribedToIncognito;
			}

			if (eventName) {
				Analytics.logUIEvent(eventName, { data });
			}

			const productName = PRODUCT_NAME_MAP[productSubtype];
			const expStatus = Context.get("purchaseExperiments")[productSubtype];
			let platform;

			if (!isApp()) {
				platform = process.env.PLATFORM;
			} else if (window.APP_PLATFORM && window.APP_PLATFORM === "android") {
				platform = "android";
			}

			let statname = `purchases - platform:${platform} - product:${productName} - purchase successful`;

			if (expStatus) {
				statname = `purchases - platform:${platform} - product:${productName} - state:${expStatus} - purchase successful`;
			}

			const queryParamData = formatQueryParamsForPurchaseCallback();

			ClientStats.update({
				statname,
				skipDelay: true,
				unique: true,
			});

			Analytics.logUIEvent(
				Analytics.eventNames.callbackStatusOnPurchaseTransaction, {
					data: {
						...analyticsData,
						...queryParamData,
					},
					type: CUSTOM_EVENT_TYPES.user_content,
				},
			);

			return;
		}

		case ERROR: {
			const { product, method, errorMessage, errorType } = action.payload;
			const {
				subPeriod,
				tokensNumber,
				productSubtype,
				productType,
				subtotal,
				discountPercentage,
			} = product;

			const data = {
				"feature": PRODUCT_FEATURE_MAP[productSubtype],
				"feature type": productType,
				"rate card name": state.rateCardName,
				"status": errorType || "failure",
				"status desc": errorMessage,
				"payment type": method === CREDIT_CARD_PAYMENT_PROCESSOR ? "cc" : PAYPAL_PAYMENT_PROCESSOR,
				"promo id": formattedCameFromParam(PRODUCT_NAME_MAP[productSubtype]),
				"came from": formattedCameFromParam(PRODUCT_NAME_MAP[productSubtype]),
				source: sourceFromPathname() || getQuery("source"),
				"braze campaign id": getQuery("brazeid"),
			};

			if (discountPercentage) {
				data["discount applied"] = discountPercentage;
			}

			if (subPeriod) {
				data["duration in months"] = subPeriod;
				data["total monthly price"] = subtotal / 100;
			}

			if (tokensNumber && productSubtype === PRODUCT_SUBTYPES.BOOST) {
				data["total token package price"] = subtotal / 100;
				data["token package"] = tokensNumber;
			}

			// android client is using this query param
			if (UrlHelpers.getParam("promoid")) {
				data["promo id"] = UrlHelpers.getParam("promoid");
			}

			const queryParamData = formatQueryParamsForPurchaseCallback();

			Analytics.logUIEvent(Analytics.eventNames.callbackStatusOnPurchaseTransaction, {
				data: {
					...data,
					...queryParamData,
				},
				type: CUSTOM_EVENT_TYPES.user_content,
			});

			return;
		}

		default: {
			return;
		}
	}
}



// WEBPACK FOOTER //
// ./src/analytics/payment.js